import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Fab, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import * as R from 'ramda'
import {
  type Constant,
  LanguageUtils,
  type Nil,
  PuiCheckbox,
  PuiTextArea,
  type Question,
  type QuestionAnswer,
  type QuestionAnswerInput,
  Text,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'

import PuiPopup, { type PuiPopupProps } from '~/components/common/PuiPopup'
import FeatureToggle from '~/constants/featureToggle'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'

import AnswerFollowUp from './AnswerFollowUp'
import { validateFollowUpQuestion } from './questionUtils'

const StyledFab = styled(Fab)(() => ({
  width: 152,
  height: 40,
}))

export interface AnswerPopupProps extends PuiPopupProps {
  answer: QuestionAnswer | Nil
  hideAnswerType?: boolean
  onAnswerAdded: (answer: QuestionAnswer) => void
  onAnswerEdited: (answer: QuestionAnswer) => void
  open: boolean
}

const AnswerPopup = ({
  answer,
  open,
  onAnswerAdded,
  onAnswerEdited,
  hideAnswerType,
  ...rest
}: AnswerPopupProps) => {
  const AnswerInputTypes: Constant[] = useSelector(getAnswerInputTypes)
  const { t } = useTranslation(['Admin', 'Common'])

  const [inputs, setInputs] = useState(answer?.inputs || [])
  const [followUpQuestion, setFollowUpQuestion] = useState<
    Partial<Question> | Nil
  >(answer?.followUpQuestion)

  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )
  const textAreaMinRows = isFollowUpQuestionsEnabled ? 2 : 1

  const isCreate = !answer
  const {
    fields: { text, followUpQuestionInputId },
    validate,
    reset,
  } = useFields(
    [
      {
        name: 'text',
        label: t('Common:TEXT_MESSAGE'),
        validators: ['required'],
        initialValue: answer?.text || '',
      },
      ...(isFollowUpQuestionsEnabled
        ? [
            {
              name: 'followUpQuestionInputId',
              label: t('Common:TEXT_MESSAGE'),
              initialValue:
                answer?.followUpQuestion?.followUp &&
                answer?.followUpQuestion?.answerInputTypeId
                  ? answer.followUpQuestion.answerInputTypeId
                  : '',
            },
          ]
        : []),
    ],
    false,
  )

  useEffect(() => {
    if (!isCreate) {
      reset()
      if (answer?.followUpQuestion?.answerInputTypeId) {
        followUpQuestionInputId?.setValue(
          answer.followUpQuestion.answerInputTypeId,
        )
      }
    }

    setFollowUpQuestion(answer?.followUpQuestion)
  }, [answer])

  useEffect(() => {
    if (!open) {
      setInputs([])
      setFollowUpQuestion(null)
      reset()
    }
  }, [open])

  useEffect(() => {
    setInputs(answer?.inputs || [])
  }, [answer?.inputs])

  const updateInputs = (checked: boolean, id: string) => {
    const oldInput = inputs.find(
      ({ inputTypeId }) => inputTypeId === id,
    ) as QuestionAnswerInput
    const oldInputIndex = inputs.indexOf(oldInput)
    const newInput = { inputTypeId: id } as QuestionAnswerInput

    if (!checked) {
      setInputs(R.without([oldInput], inputs))
    } else if (oldInputIndex > -1) {
      setInputs(R.update(oldInputIndex, newInput, inputs))
    } else {
      setInputs(inputs.concat(newInput))
    }
  }

  const add = () => {
    if (validate()) {
      const validFollowUpQuestion = validateFollowUpQuestion(
        followUpQuestion,
        AnswerInputTypes,
      )

      const newAnswer = {
        ...(answer || {}),
        followUpQuestion: validFollowUpQuestion,
        inputs,
        text: text.value,
      } as QuestionAnswer

      if (isCreate) {
        onAnswerAdded(newAnswer)
      } else {
        onAnswerEdited(newAnswer)
      }
    }
  }

  return (
    <PuiPopup
      DialogProps={{
        maxWidth: false,
        PaperProps: {
          sx: { width: 650 },
        },
      }}
      open={open}
      {...rest}
    >
      <Grid container direction="column" p={3}>
        <Grid item>
          <Text variant="h2">
            {isCreate ? t('Common:ADD_ANSWER') : t('Common:EDIT_ANSWER')}
          </Text>
        </Grid>
        <Grid item mb={2} mt={1}>
          <PuiTextArea
            field={text}
            minRows={textAreaMinRows}
            placeholder={t('Admin:CATALOG.ANSWER_POPUP.TEXT_FIELD_PLACEHOLDER')}
          />
          {isFollowUpQuestionsEnabled && (
            <AnswerFollowUp
              followUpQuestion={followUpQuestion}
              followUpQuestionInputId={followUpQuestionInputId}
              setFollowUpQuestion={setFollowUpQuestion}
            />
          )}
        </Grid>
        {hideAnswerType
          ? null
          : AnswerInputTypes.map((type) => (
              <Grid item key={type.id}>
                <PuiCheckbox
                  checked={inputs.some(
                    ({ inputTypeId }) => inputTypeId === type.id,
                  )}
                  label={t('Admin:CATALOG.ANSWER_POPUP.INCLUDE_A_NAME', {
                    name: LanguageUtils.getTranslatedFieldName(
                      type,
                    ).toLowerCase(),
                  })}
                  onChange={Utils.handleFormCheckboxInput((checked) =>
                    updateInputs(checked, type.id),
                  )}
                />
              </Grid>
            ))}
        <Grid item mt={1}>
          <StyledFab
            color="inherit"
            type="submit"
            variant="extended"
            onClick={add}
          >
            {isCreate ? t('Common:ADD_ACTION') : t('Common:SAVE_ACTION')}
          </StyledFab>
        </Grid>
      </Grid>
    </PuiPopup>
  )
}

export default AnswerPopup
